import { useMemo, useRef, useState, type FC } from 'react';
import DraggableModal from 'components/organisms/draggableModal';
import BaseButton from 'components/atoms/baseButton';
import defaultThreshold from 'constants/defaultThreshold';
import common from 'constants/common';
import { ReadingType } from 'infrastructure/enums';

import { scrollToErrorElement } from '../helpers';
import { useCriticalReadingLogic } from './use-logic';
import { useClinicalRunbook } from './use-clinical-runbook';
import StepRenderer from './steps/step-renderer';
import SymptomsStep from './steps/symptoms-step';
import WeightReview from './weight-management/weight-review';
import { deriveDirection } from './engine/classify-recheck';
import { SLEEPING_MAT_TYPES } from './engine/step-machine';
import s from './styles.module.scss';

import type { ICriticalReadingModalProps } from './types';
import type { OutOfRangeParam, ReadingContext } from './engine/types';
import type { IPatientThresholds } from 'infrastructure/interfaces';
import type {
  TStatuses,
  Value,
} from 'infrastructure/interfaces/i-rpm-patient-activity';

/**
 * Per-parameter [minRisk, maxRisk] bounds used to decide high vs low direction.
 * Prefers this reading's per-patient thresholds; falls back to the global
 * defaultThreshold when a patient value is missing.
 * - Sleep Mat readings report their pulse under the `pulse` key but must use the
 *   sleep-pulse bounds (min/maxRiskSleepPulse), so `pulse` is switched by readingType.
 * - oxygen/pef/fev1 are low-only (deriveDirection short-circuits them to 'low'); their
 *   single risk bound is still filled in for completeness.
 * - temperature uses the critical band from common (split at 99°F: Fever vs Hypothermia).
 * - weight has no symptom checklist and change-based thresholds, so no bounds apply.
 */
const getRiskBounds = (
  t: IPatientThresholds,
  readingType: ReadingType,
): Record<string, [number | undefined, number | undefined]> => {
  const isSleepMat = SLEEPING_MAT_TYPES.has(readingType);
  return {
    systolic: [
      t?.minRiskSystolic ?? defaultThreshold.minRiskSystolic,
      t?.maxRiskSystolic ?? defaultThreshold.maxRiskSystolic,
    ],
    diastolic: [
      t?.minRiskDiastolic ?? defaultThreshold.minRiskDiastolic,
      t?.maxRiskDiastolic ?? defaultThreshold.maxRiskDiastolic,
    ],
    pulse: isSleepMat
      ? [
          t?.minRiskSleepPulse ?? defaultThreshold.minRiskSleepPulse,
          t?.maxRiskSleepPulse ?? defaultThreshold.maxRiskSleepPulse,
        ]
      : [
          t?.minRiskPulse ?? defaultThreshold.minRiskPulse,
          t?.maxRiskPulse ?? defaultThreshold.maxRiskPulse,
        ],
    glucose: [
      t?.minRiskGlucose ?? defaultThreshold.minRiskGlucose,
      t?.maxRiskGlucose ?? defaultThreshold.maxRiskGlucose,
    ],
    oxygen: [t?.riskBloodoxygen ?? defaultThreshold.riskBloodoxygen, undefined],
    pef: [t?.minRiskPef ?? defaultThreshold.minRiskPef, undefined],
    fev1: [undefined, undefined],
    temperature: [
      common.temperatureCriticals.min,
      common.temperatureCriticals.max,
    ],
    weight: [undefined, undefined],
  };
};

/** Build engine ReadingContext from a single reading's values/statuses: out-of-range params + direction. */
const buildContext = (
  readingType: ReadingType,
  values: Value,
  statuses: TStatuses,
  thresholds: IPatientThresholds,
): ReadingContext => {
  const statusMap = (statuses ?? {}) as Record<string, string>;
  const value = (values ?? {}) as Record<string, number>;
  const riskBounds = getRiskBounds(thresholds, readingType);
  const outOfRangeParams: OutOfRangeParam[] = Object.entries(statusMap)
    .filter(([, level]) => level === 'risk' || level === 'critical')
    .map(([param, level]) => {
      const [minRisk, maxRisk] = riskBounds[param] ?? [undefined, undefined];
      return {
        param,
        level: level as OutOfRangeParam['level'],
        direction: deriveDirection(param, value[param], minRisk, maxRisk),
      };
    });
  return { readingType, outOfRangeParams };
};

/**
 * Router: a regular (non-CHF) Weight critical reading uses the separate Weight
 * Management review flow; everything else uses the #1–#6 guided wizard. CHF/Fluid
 * weight readings never reach here (no Nurse-MA button — routed to the nurse template).
 */
const NurseMaCriticalReadingModal: FC<ICriticalReadingModalProps> = (props) => {
  const { readingType, idx, patientGuid, onClose } = props;
  if (readingType === ReadingType.Weight) {
    return (
      <WeightReview idx={idx} patientGuid={patientGuid} onClose={onClose} />
    );
  }
  return <WizardBody {...props} />;
};

const WizardBody: FC<ICriticalReadingModalProps> = ({
  idx,
  patientGuid,
  readingType,
  values,
  statuses,
  thresholds,
  isRecovered,
  onClose,
}) => {
  const readingContext = useMemo<ReadingContext>(
    () => buildContext(readingType, values, statuses, thresholds),
    [readingType, values, statuses, thresholds],
  );
  const runbook = useClinicalRunbook({ patientGuid });

  const {
    formik,
    engineState,
    buildItemsForStep,
    handleSave,
    errors,
    validateStep,
    clearErrors,
    Alert,
  } = useCriticalReadingLogic({
    patientGuid,
    readingContext,
    idx,
    runbook,
    isRecovered,
  });
  const [stepIndex, setStepIndex] = useState(0);
  const formWrapperRef = useRef<HTMLDivElement>(null);

  const { visibleSteps } = engineState;
  const clampedIndex = Math.min(stepIndex, visibleSteps.length - 1);
  const activeStep = visibleSteps[clampedIndex] ?? visibleSteps[0];
  const isOutcomeStep = activeStep === 'outcome';

  const items = useMemo(
    () => buildItemsForStep(activeStep),
    [buildItemsForStep, activeStep],
  );

  const onBack = () => {
    clearErrors();
    setStepIndex((i) => Math.max(i - 1, 0));
  };
  const onNext = () => {
    // Block advancing until the current step's required fields are filled.
    if (validateStep(activeStep).length > 0) {
      scrollToErrorElement(formWrapperRef.current);
      return;
    }
    clearErrors();
    setStepIndex((i) => Math.min(i + 1, visibleSteps.length - 1));
  };
  const onSave = async () => {
    const ok = await handleSave();
    if (!ok) {
      scrollToErrorElement(formWrapperRef.current);
      return;
    }
    onClose();
  };

  const footer = (
    <div className={s.footer}>
      <div className={s['footer-left']}>
        <BaseButton
          type="secondary"
          label="Back"
          disabled={stepIndex === 0}
          onClick={onBack}
          width={100}
        />
        <BaseButton
          type="secondary"
          label="Next"
          disabled={isOutcomeStep}
          onClick={onNext}
          width={100}
        />
      </div>
      <div className={s['footer-right']}>
        <BaseButton
          label="Save"
          disabled={!isOutcomeStep}
          onClick={onSave}
          width={100}
        />
      </div>
    </div>
  );

  return (
    <DraggableModal
      title="Critical Reading Review"
      isOpen
      toggle={onClose}
      footer={footer}
    >
      <div className={s.wrapper} ref={formWrapperRef}>
        {activeStep === 'symptoms' ? (
          <SymptomsStep
            formik={formik}
            outOfRangeParams={readingContext.outOfRangeParams}
            errors={errors}
          />
        ) : (
          <StepRenderer items={items} formik={formik} errors={errors} />
        )}
        {Alert}
      </div>
    </DraggableModal>
  );
};

export default NurseMaCriticalReadingModal;
